<script>
  import Image from "#lib/Image.svelte";

  const ZOOM_IN_DURATION = 500;
  const ZOOM_OUT_DURATION = 300;
  /* Arrow key navigation between zoomed images: a plain crossfade. Anything with movement in it
     competes with the photograph you just asked to look at. */
  const NAV_DURATION = 260;
  const SCROLL_THRESHOLD_PERCENT = 0.3; // Trigger scroll if center is > 30% off viewport center
  const MOBILE_THRESHOLD = 768;
  // Containers that group images into one gallery for arrow key navigation
  const GALLERY_SELECTORS = '.entry-previews, .panorama-grid-collage, .story-block';

  let {
    /** @type {boolean} enable image zoom on click (lightbox) */
    zoom = true,
    /** @type {boolean} disable zoom on mobile */
    disable_zoom_on_mobile = true,
    title,
    description,
    preamble,
    width: img_width,
    height: img_height,
    media_type,
    href,
    is_editing,
    style,
    ...rest
  } = $props();

  let clone = $state(),
    overlay = $state();
  let sizes_upgrade_timeout = $state();

  let is_zoomed = $state(false);

  let scroll_position = $state();
  /**
   * Geometry of the image currently shown in the zoomed view. Changes when the
   * user navigates to a sibling image with the arrow keys.
   * @type {{root: HTMLElement, media_element: HTMLElement, thumb: DOMRect, crop: {left: number, top: number, width: number, height: number}, border_radius: number, aspect_ratio: number, start_scroll_y: number} | null}
   */
  let session = null;
  /** @type {HTMLElement[]} clones still fading out from a navigation */
  let fading_clones = [];
  /**
   * Furthest the page can scroll, captured before the body is locked. Once the body is
   * `position: fixed` it is out of flow, so scrollHeight no longer reports the document's
   * real height and this cannot be recomputed while zoomed.
   */
  let max_scroll_position = 0;

  function preventScroll(e) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  function enableScroll() {
    // Remove event listeners
    document.body.removeEventListener('wheel', preventScroll);
    document.body.removeEventListener('touchmove', preventScroll);

    // Remove any lingering transition from zoom_out scroll animation
    document.body.style.transition = '';

    // Unlock the body
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    // Restore scroll position
    window.scrollTo(0, scroll_position);
  }

  /** Fit an element into the viewport while maintaining its aspect ratio */
  function apply_fullscreen(element, aspect_ratio) {
    const viewport_width = window.innerWidth;
    const viewport_height = window.innerHeight;

    let final_width, final_height, final_left, final_top;

    // Determine final dimensions to fit within viewport while maintaining aspect ratio
    if (viewport_width / viewport_height < aspect_ratio) {
      final_width = viewport_width;
      final_height = viewport_width / aspect_ratio;
      final_left = 0;
      final_top = (viewport_height - final_height) / 2;
    } else {
      final_height = viewport_height;
      final_width = viewport_height * aspect_ratio;
      final_left = (viewport_width - final_width) / 2;
      final_top = 0;
    }

    // Set the dimensions and position of the element
    element.style.left = `${final_left}px`;
    element.style.top = `${final_top}px`;
    element.style.width = `${final_width}px`;
    element.style.height = `${final_height}px`;
    element.style.clipPath = 'inset(0 0 0 0 round 0px)';
  }

  /**
   * Resolve the visual media node used for zoom.
   * Responsive images wrap an <img> in <picture>; both share .media-item, so
   * querySelector would return the picture while clicks land on the img.
   * @param {HTMLElement} button
   * @returns {HTMLElement | null}
   */
  function resolve_media_element(button) {
    const el = button.querySelector('.media-item');
    if (!el) return null;
    if (el.tagName.toLowerCase() === 'picture') {
      return el.querySelector('img') || el;
    }
    return el;
  }

  /**
   * Intrinsic aspect ratio of a media element, falling back to its layout box.
   * @param {HTMLElement} media_element
   * @param {number} [preferred] known ratio (e.g. from props), used when valid
   */
  function get_aspect_ratio(media_element, preferred) {
    if (Number.isFinite(preferred) && preferred > 0) return preferred;

    const is_video = media_element.tagName.toLowerCase() === 'video';
    const natural = is_video
      ? media_element.videoWidth / media_element.videoHeight
      : media_element.naturalWidth / media_element.naturalHeight;
    if (Number.isFinite(natural) && natural > 0) return natural;

    const attribute = parseFloat(media_element.getAttribute('width')) / parseFloat(media_element.getAttribute('height'));
    if (Number.isFinite(attribute) && attribute > 0) return attribute;

    const rect = media_element.getBoundingClientRect();
    return rect.width / rect.height;
  }

  /**
   * How far the page is scrolled right now. While zoomed the body is locked with a
   * negative top offset, which may still be animating towards `scroll_position`.
   */
  function get_current_offset() {
    if (document.body.style.position !== 'fixed') return window.pageYOffset;
    return -parseFloat(getComputedStyle(document.body).top) || 0;
  }

  /**
   * Rotation an element declares, in degrees, from either the `rotate` property or a `transform`
   * matrix. Collage-style layouts tilt the whole card, so the image inside is turned with it.
   */
  function read_rotation(style) {
    const rotate = parseFloat(style.rotate);
    if (Number.isFinite(rotate) && rotate !== 0) return rotate;

    const matrix = style.transform;
    if (!matrix || matrix === 'none') return 0;
    const values = matrix.match(/matrix\(([^)]+)\)/)?.[1]?.split(',').map(Number);
    if (!values || values.length < 4) return 0;
    return Math.atan2(values[1], values[0]) * (180 / Math.PI);
  }

  /**
   * The rotation an element is subject to, and the point it turns about — the centre of the
   * nearest rotated ancestor, since that is what the tilt is applied to (transform-origin is
   * centre by default). Only that one rotation is resolved; nested tilts are not a thing here.
   */
  function get_rotation(element) {
    for (let node = element; node && node !== document.body; node = node.parentElement) {
      const angle = read_rotation(getComputedStyle(node));
      if (!angle) continue;
      const rect = node.getBoundingClientRect();
      return { angle, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }
    return { angle: 0, x: 0, y: 0 };
  }

  /**
   * Geometry of an element as if it were not rotated.
   *
   * getBoundingClientRect on a rotated element reports the axis-aligned box AROUND it, which is
   * both bigger than the element and centred elsewhere. Flying a clone from that box is what made
   * a tilted thumbnail jump straight before it started to zoom. The centre of the rotated box maps
   * back to the untransformed centre by turning it back about the pivot, and the untransformed
   * size comes from the used width/height rather than from the box.
   */
  function unrotated_rect(element, rotation) {
    const rect = element.getBoundingClientRect();
    if (!rotation.angle) return rect;

    const radians = -rotation.angle * (Math.PI / 180);
    const dx = rect.left + rect.width / 2 - rotation.x;
    const dy = rect.top + rect.height / 2 - rotation.y;
    const centre_x = rotation.x + dx * Math.cos(radians) - dy * Math.sin(radians);
    const centre_y = rotation.y + dx * Math.sin(radians) + dy * Math.cos(radians);

    const style = getComputedStyle(element);
    const width = parseFloat(style.width) || element.offsetWidth || rect.width;
    const height = parseFloat(style.height) || element.offsetHeight || rect.height;

    return { left: centre_x - width / 2, top: centre_y - height / 2, width, height };
  }

  /**
   * Capture the geometry needed to fly a .zoomable-image in and out of the zoomed view.
   * @param {HTMLElement} root
   * @param {number} [preferred_aspect_ratio]
   */
  function capture_target(root, preferred_aspect_ratio) {
    const media_element = resolve_media_element(root);
    if (!media_element) return null;

    // Everything below is measured in the untilted frame; the clone carries the tilt as its own
    // `rotate` and animates it out, so the two views line up exactly at both ends.
    const rotation = get_rotation(media_element);
    const button_rect = unrotated_rect(root, rotation);
    const thumb = unrotated_rect(media_element, rotation); // Image position relative to viewport
    const button_style = getComputedStyle(root);
    const img_style = getComputedStyle(media_element);

    return {
      root,
      media_element,
      rotation: rotation.angle,
      thumb,
      // Position within the page, so scrolling while zoomed doesn't invalidate it
      document_top: thumb.top + get_current_offset(),
      // How much of the image the layout crops away, used to clip on the way out
      crop: {
        left: button_rect.left - thumb.left,
        top: button_rect.top - thumb.top,
        width: button_rect.width,
        height: button_rect.height
      },
      border_radius: Math.max(parseInt(button_style.borderRadius) || 0, parseInt(img_style.borderRadius) || 0),
      aspect_ratio: get_aspect_ratio(media_element, preferred_aspect_ratio)
    };
  }

  /**
   * Build a fixed-position clone sitting exactly on top of the original media element.
   * @param {HTMLElement} media_element
   * @param {{left: number, top: number, width: number, height: number}} rect untilted box to start from
   * @param {number} rotation degrees the clone starts turned by, so it covers a tilted card exactly
   */
  function create_clone(media_element, rect, rotation) {
    const element = media_element.cloneNode(true);
    const img_style = getComputedStyle(media_element);

    element.style.objectFit = img_style.objectFit;
    element.style.objectPosition = img_style.objectPosition;

    // Copy video-specific properties if the media is a video
    if (media_element.tagName.toLowerCase() === 'video') {
      // Explicitly set muted state as it's not copied by cloneNode due to a Browser Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=1424871
      element.muted = media_element.muted;
      // Enable native video controls for the zoomed clone
      element.controls = true;
      media_element.pause();
      // always play when zooming in
      element.play();
    }

    element.style.position = 'fixed';
    element.style.visibility = 'visible'; // the original may already be hidden
    element.style.left = `${rect.left}px`;
    element.style.top = `${rect.top}px`;
    element.style.width = `${rect.width}px`;
    element.style.height = `${rect.height}px`;
    element.style.rotate = `${rotation}deg`;
    element.style.clipPath = 'none';
    element.style.transition = 'none';
    element.classList.add('zoomed-clone');

    return element;
  }

  /** Request the full-viewport source for a zoomed clone */
  function upgrade_clone_source(element) {
    if (!element || element.tagName?.toLowerCase() !== 'img') return;
    element.setAttribute('sizes', '100vw');
    element.setAttribute('loading', 'eager');
    element.setAttribute('fetchpriority', 'high');
  }

  /** Show an original media element again, resuming video playback where the clone left off */
  function restore_original(target, from_clone) {
    if (!target) return;
    target.media_element.style.visibility = 'visible';
    if (from_clone && target.media_element.tagName.toLowerCase() === 'video') {
      target.media_element.currentTime = from_clone.currentTime;
      target.media_element.play();
    }
  }

  /**
   * Scroll the (locked) page so the target ends up in the middle of the viewport.
   * Keeps the page in sync with what's zoomed, so zooming out lands on the thumbnail.
   */
  function center_page_on(target, duration) {
    const current_offset = get_current_offset();
    const image_center_y = target.document_top + target.thumb.height / 2;
    const viewport_center_y = current_offset + window.innerHeight / 2;
    const scroll_difference = image_center_y - viewport_center_y;
    if (Math.abs(scroll_difference) <= window.innerHeight * SCROLL_THRESHOLD_PERCENT) return;

    // Clamp to the scrollable range. Unclamped, centring on an image near the top of the
    // document yields a negative offset, and `-${negative}px` is the invalid CSS string
    // "--100px": the body offset is silently not applied, while zoom_out and enableScroll
    // still trust the value — so the clone flies to the wrong place and the page jumps.
    // Reachable by wrapping from the last gallery image round to the first.
    scroll_position = Math.min(Math.max(current_offset + scroll_difference, 0), max_scroll_position);
    if (scroll_position === current_offset) return;
    // "Scrolling" while locked means transitioning the body's top offset
    document.body.style.transition = `top ${duration}ms var(--transition-spring-snap)`;
    document.body.style.top = `-${scroll_position}px`;
  }

  /** Zoom-enabled images of the same gallery, in document order */
  function get_gallery_items(root) {
    const scope = root.closest(GALLERY_SELECTORS) || document.body;
    return Array.from(scope.querySelectorAll('.zoomable-image[data-zoomable="true"]'))
      // Skip images hidden by the layout - they have no thumbnail to fly back to
      .filter(element => element.getClientRects().length > 0 && resolve_media_element(element));
  }

  /**
   * Swap the zoomed view to the previous/next image of the same gallery.
   * @param {1 | -1} direction
   */
  function navigate(direction) {
    if (!is_zoomed || !session) return;

    const items = get_gallery_items(session.root);
    const index = items.indexOf(session.root);
    if (index === -1 || items.length < 2) return;

    const next_root = items[(index + direction + items.length) % items.length];
    const next = capture_target(next_root);
    if (!next || next.media_element === session.media_element) return;

    const previous = session;
    const previous_clone = clone;
    session = next;

    // Drop clones still fading out from an earlier, rapid navigation
    while (fading_clones.length) fading_clones.pop().remove();

    // Hide the incoming thumbnail, it is represented by the clone from now on
    next.media_element.style.visibility = 'hidden';

    // The incoming clone starts at its final size and untilted, transparent
    clone = create_clone(next.media_element, next.thumb, 0);
    apply_fullscreen(clone, next.aspect_ratio);
    clone.style.opacity = 0;
    upgrade_clone_source(clone); // already full screen, no need to wait
    clone.addEventListener('click', zoom_out);
    document.body.appendChild(clone);

    if (previous_clone) {
      fading_clones.push(previous_clone);
      previous_clone.removeEventListener('click', zoom_out);
      previous_clone.style.transition = `opacity ${NAV_DURATION}ms ease`;
      previous_clone.style.opacity = 0;

      setTimeout(() => {
        const pending = fading_clones.indexOf(previous_clone);
        if (pending !== -1) fading_clones.splice(pending, 1);
        previous_clone.remove();
        // Navigating back and forth can make this the zoomed image again
        if (previous.media_element !== session?.media_element) restore_original(previous, previous_clone);
      }, NAV_DURATION);
    }

    // Force a reflow so the start styles apply before the transition starts
    clone.offsetHeight;

    requestAnimationFrame(() => {
      clone.style.transition = `opacity ${NAV_DURATION}ms ease`;
      clone.style.opacity = 1;
    });

    center_page_on(next, NAV_DURATION);
  }

  /** Reverse the zoom effect, flying the clone back onto its thumbnail */
  function zoom_out() {
    if (!is_zoomed || !session) return; // Prevent double execution
    is_zoomed = false; // Set flag immediately

    // Cancel any pending sizes upgrade
    if (sizes_upgrade_timeout) {
      clearTimeout(sizes_upgrade_timeout);
      sizes_upgrade_timeout = null;
    }

    const target = session;
    const active_clone = clone;
    const active_overlay = overlay;

    // Drop clones still fading out from a navigation
    while (fading_clones.length) fading_clones.pop().remove();

    // Where the thumbnail sits now, after the scrolling that happened while zoomed
    const target_top = target.document_top - scroll_position;
    const { crop, thumb } = target;

    // zoom out styles
    active_clone.style.transition = `all ${ZOOM_OUT_DURATION}ms var(--transition-spring-snap)`;
    active_clone.style.left = `${thumb.left}px`; // thumb is relative to viewport, no need to adjust for scroll
    active_clone.style.top = `${target_top}px`;
    active_clone.style.width = `${thumb.width}px`;
    active_clone.style.height = `${thumb.height}px`;
    active_clone.style.rotate = `${target.rotation}deg`; // back onto a tilted card, if it is one
    active_clone.style.clipPath =`inset(${crop.top}px ${thumb.width - crop.left - crop.width}px ${thumb.height - crop.top - crop.height}px ${crop.left}px round ${target.border_radius}px)`;
    active_overlay.style.opacity = 0;

    window.removeEventListener('resize', handle_resize);
    window.removeEventListener('keydown', handle_keydown);

    setTimeout(() => {
      restore_original(target, active_clone);

      // Cleanup event listeners
      active_clone.removeEventListener('click', zoom_out);
      active_overlay.removeEventListener('click', zoom_out);

      active_clone.remove();
      active_overlay.remove();

      // Enable scrolling - uses the latest 'scroll_position' value,
      // which follows the image the user navigated to
      enableScroll();

      session = null;
      clone = undefined;
      overlay = undefined;
    }, ZOOM_OUT_DURATION);
  }

  function handle_keydown(event) {
    if (!is_zoomed) return;
    if (event.key === 'Tab') {
      event.preventDefault();
    } else if (event.key === 'Escape') {
      zoom_out();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      navigate(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      navigate(-1);
    }
  }

  /** Toggle the zoom-in and zoom-out effect for the image. */
  function toggle_zoom(event) {
    if (is_zoomed) return; // Prevent re-triggering zoom

    const button_element = event.currentTarget;
    const media_element = resolve_media_element(button_element);
    if (!media_element) return;

    // Handle keyboard events
    if (event.type === 'keydown') {
      event.preventDefault();
      // Simulate a click on the media element
      media_element.click();
      return;
    }

    // Ignore clicks on siblings (e.g. video playback controls), but accept
    // the media node itself or anything inside it (e.g. picture > img).
    if (event.target !== media_element && !media_element.contains(event.target)) {
      event.preventDefault();
      return;
    }

    // Capture geometry **before** locking the body
    const target = capture_target(button_element, img_width / img_height);
    if (!target) return;

    is_zoomed = true; // Set flag early
    session = target;

    // Create a clone for the zoom effect, sitting exactly on the thumbnail — tilt included
    clone = create_clone(media_element, target.thumb, target.rotation);

    // Create an overlay
    overlay = document.createElement('div');
    overlay.classList.add('zoomable-image-overlay');
    overlay.style.opacity = 0;
    document.body.appendChild(overlay);

    // Add Clone
    document.body.appendChild(clone);

    // Hide the original image
    media_element.style.visibility = 'hidden';

    // --- Manual Scroll Lock (adapted from disableScroll) ---
    scroll_position = window.pageYOffset; // Position *before* any potential scroll animation
    // Measured while the body is still in flow; see max_scroll_position.
    max_scroll_position = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    // Lock the body using the *initial* scroll position
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scroll_position}px`;
    document.body.style.width = '100%';

    // Add scroll prevention listeners AFTER fixing position
    document.body.addEventListener('wheel', preventScroll, { passive: false });
    document.body.addEventListener('touchmove', preventScroll, { passive: false });
    // --- End Manual Scroll Lock ---

    clone.addEventListener('click', zoom_out);
    overlay.addEventListener('click', zoom_out);
    window.addEventListener('keydown', handle_keydown);
    // Add resize listener *before* starting animation
    window.addEventListener('resize', handle_resize);

    // Force a reflow to ensure initial styles are applied before transition starts
    clone.offsetHeight; // Reading a property forces reflow

    // apply the zoom effect and potential scroll animation
    requestAnimationFrame(() => {
      // Start zoom animation — the tilt unwinds over the same curve as the box
      clone.style.transition = `all ${ZOOM_IN_DURATION}ms var(--transition-spring-snap)`;
      clone.style.rotate = '0deg';
      apply_fullscreen(clone, target.aspect_ratio);
      overlay.style.opacity = 'var(--backdrop-opacity, 0.8)';

      // Start scroll animation (if needed) by transitioning body's top offset
      center_page_on(target, ZOOM_IN_DURATION);

      // After zoom-in completes, upgrade the clone to high-res source
      sizes_upgrade_timeout = setTimeout(() => {
        if (is_zoomed) upgrade_clone_source(clone);
      }, ZOOM_IN_DURATION);
    });
  }

  /** Handle browser resize event */
  function handle_resize() {
    // Only adjust if currently zoomed and clone exists
    if (is_zoomed && clone && session) {
      apply_fullscreen(clone, session.aspect_ratio);

      // Optional: Recalculate if scroll is still needed and adjust body top?
      // For simplicity, let's keep the existing scroll offset. apply_fullscreen re-centers the image visually within the new viewport.
    }
  }
</script>

<div
  class='zoomable-image'
  {...(!is_editing ? {
    onclick: (event) => {
      const inside_anchor = !!event.currentTarget.closest('a');
      if ((event.metaKey || event.ctrlKey) && !inside_anchor && href) {
        window.open(href, '_self');
      } else if (zoom && !is_zoomed) {
        if(disable_zoom_on_mobile && window.innerWidth < MOBILE_THRESHOLD){
          return;
        }
        toggle_zoom(event);
      }
    },
    onkeydown: (event) => {
      if (zoom && !is_zoomed && [' ', 'Enter'].includes(event.key)) {
        if(disable_zoom_on_mobile && window.innerWidth < MOBILE_THRESHOLD){
          return;
        }
        event.preventDefault();
        toggle_zoom(event);
        event.currentTarget.blur();
      }
    },
    tabindex: '0'
  } : {
    onclick: (event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(href, '_blank');
      }
    },
  })}
  style:cursor={zoom && !is_zoomed ? 'zoom-in' : 'inherit'}
  role='button'
  style={style}
  data-zoomable={zoom && !is_editing ? 'true' : undefined}
>
  <Image 
    {...rest} 
    {media_type}
    {is_editing}
    {zoom}
    width={img_width}
    height={img_height}
  />
</div>

<style lang='postcss'>
  :global(:root) {
    --image-zoom-duration: 500ms;
  }
  .zoomable-image {
    width: 100%;
    height: 100%;
    display: flex;
    position: relative;
    -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */
  }
  :global(.zoomed-clone) {
    cursor: zoom-out !important;
    z-index: 10010;
    transform-origin: center;
    will-change: rotate, width, height, left, top, clip-path;
    border: none !important;
  }

  :global(.zoomed-clone.zoomed) {
    transition: 
      all var(--image-zoom-duration) var(--transition-spring-snap),
      clip-path var(--image-zoom-duration) var(--transition-spring-snap);
  }

  :global(.zoomable-image-overlay) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: var(--backdrop-background, var(--color-default-foreground));
    transition: opacity var(--image-zoom-duration) var(--transition-spring-snap);
    z-index: 10009;
  }
</style>
